import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from 'react';

export interface BannerOptions {
  variant?: 'info' | 'success' | 'warning';
  message: string;
  durationMs?: number;
}

interface InlineBannerContextValue {
  banner: BannerOptions | null;
  showBanner: (opts: BannerOptions) => void;
  dismissBanner: () => void;
}

const InlineBannerContext = createContext<InlineBannerContextValue | undefined>(undefined);

export function InlineBannerProvider({ children }: { children: ReactNode }) {
  const [banner, setBanner] = useState<BannerOptions | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const dismissBanner = useCallback(() => {
    setBanner(null);
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const showBanner = useCallback((opts: BannerOptions) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setBanner(opts);
    const duration = opts.durationMs ?? 3000;
    timerRef.current = setTimeout(() => setBanner(null), duration);
  }, []);

  return (
    <InlineBannerContext.Provider value={{ banner, showBanner, dismissBanner }}>
      {children}
    </InlineBannerContext.Provider>
  );
}

export function useInlineBanner() {
  const ctx = useContext(InlineBannerContext);
  if (!ctx) throw new Error('useInlineBanner must be used within InlineBannerProvider');
  return ctx;
}
